/** Booking checklist rendering — port of renderBL() over computeChecklist(),
 * annotated with the date/time each reservation needs to be for. */
import { computeChecklist, type Placement } from "@trip/shared/engine/checklist";
import { computeTrip, type SlotRowComp } from "@trip/shared/engine/schedule";
import { esc, fmin, safeUrl } from "@trip/shared/engine/time";
import { isFixed } from "@trip/shared/types";
import { store } from "../store.js";
import { isEditing } from "./editor.js";
import { ico } from "./icons.js";

/** actId → where it currently sits in the itinerary (day + computed start). */
function placements(): Record<string, Placement> {
  const def = store.def;
  const trip = computeTrip(def, store.slots, store.dayEnds, store.fixDurs);
  const out: Record<string, Placement> = {};
  for (const d of def.days) {
    const c = trip.days[d.id];
    d.items.forEach((it, i) => {
      if (isFixed(it)) return;
      const st = store.slots[`${d.id}_${i}`];
      const row = c.rows[i] as SlotRowComp | undefined;
      if (!st || !row || row.skipped) return;
      out[st.act] = { dayLabel: `${d.dow} ${d.date}`, timeLabel: fmin(row.startMin) };
    });
    (c.extra?.keys ?? []).forEach((key, k) => {
      const st = store.slots[key];
      const row = c.rows[d.items.length + k] as SlotRowComp | undefined;
      if (!st || !row || row.skipped) return;
      out[st.act] = { dayLabel: `${d.dow} ${d.date}`, timeLabel: fmin(row.startMin) };
    });
  }
  return out;
}

export function renderChecklist() {
  const { items } = computeChecklist(store.def, store.slots, store.checks, placements());
  const root = document.getElementById("booklist")!;
  root.querySelectorAll(".bl-item, .bl-empty, .bl-edit").forEach((n) => n.remove());
  const disabled = store.role !== "edit" ? " disabled" : "";
  const editing = isEditing();
  const html = items.length
    ? items
        .map(
          (b) => `
    <div class="bl-item${b.checked ? " done" : ""}${b.orphan ? " orphan" : ""}" id="bl_${b.k}">
      <input type="checkbox" ${b.checked ? "checked" : ""}${disabled} data-action="bl" data-k="${b.k}" aria-label="done">
      <div class="bl-what"><span class="bl-when ${b.cls}">${b.when}</span>&nbsp; <b>${esc(b.what)}</b>${
        b.scheduled
          ? `<span class="bl-for" title="Book it for this date and time">${ico.calendar} ${esc(b.scheduled.dayLabel)} · ${esc(b.scheduled.timeLabel)}</span>`
          : ""
      }<small>${esc(b.why)}</small>${
        b.orphan
          ? `<small class="orph">⚠ BOOKED, but no longer marked 🔒 in the itinerary — re-select it there, or cancel the reservation and untick this.</small>`
          : ""
      }</div>
      <div class="bl-link">${
        editing
          ? `<button class="btn-det btn-sm" data-action="ed-bl-edit" data-k="${b.k}" title="Edit this booking item">✎</button>`
          : safeUrl(b.url) ? `<a href="${safeUrl(b.url)}" target="_blank" rel="noopener">${esc(b.link)} ↗</a>` : ""
      }</div>
    </div>`
        )
        .join("")
    : `<div class="bl-empty" style="padding:14px 22px;font-size:13px;color:var(--mut)">Mark a decision ✔ Confirmed in the itinerary below and its booking to-do will appear here. Nothing is confirmed yet.</div>`;
  root.insertAdjacentHTML("beforeend", html);
  if (editing) {
    root.insertAdjacentHTML(
      "beforeend",
      `<div class="bl-edit" style="padding:10px 22px"><button class="btn-det" data-action="ed-bl-add">＋ Add booking item</button></div>`
    );
  }
}
